import React, { useState, useEffect, useRef } from "react";
import styles from "./ModelSettingsPopup.module.css";
import { ProviderIcon, CheckIcon } from "./modelIcons";

// ─── Enums & types ──────────────────────────────────────────────────────────

export enum ReasoningEffort {
  Fast = "Fast",
  Normal = "Normal",
  Thinking = "Thinking",
  Pro = "Pro",
}

export type ModelProvider = "openai" | "anthropic";

export const MODEL_REASONING_MAP: Record<
  string,
  Record<ReasoningEffort, string>
> = {
  "gpt-6.1-sol": {
    [ReasoningEffort.Fast]: "low",
    [ReasoningEffort.Normal]: "medium",
    [ReasoningEffort.Thinking]: "high",
    [ReasoningEffort.Pro]: "xhigh",
  },
  "gpt-5.6-terra": {
    [ReasoningEffort.Fast]: "none",
    [ReasoningEffort.Normal]: "low",
    [ReasoningEffort.Thinking]: "medium",
    [ReasoningEffort.Pro]: "high",
  },
  "databricks-claude-opus-4-6": {
    [ReasoningEffort.Fast]: "none",
    [ReasoningEffort.Normal]: "low",
    [ReasoningEffort.Thinking]: "medium",
    [ReasoningEffort.Pro]: "high",
  },
  "databricks-claude-sonnet-4-6": {
    [ReasoningEffort.Fast]: "none",
    [ReasoningEffort.Normal]: "low",
    [ReasoningEffort.Thinking]: "medium",
    [ReasoningEffort.Pro]: "high",
  },
};

export const MODEL_REASONING_REVERSE_MAP: Record<
  string,
  Record<string, ReasoningEffort>
> = Object.fromEntries(
  Object.entries(MODEL_REASONING_MAP).map(([model, mapping]) => [
    model,
    Object.fromEntries(
      Object.entries(mapping).map(([effort, value]) => [
        value,
        effort as ReasoningEffort,
      ]),
    ),
  ]),
);

// ─── Model config ───────────────────────────────────────────────────────────

export type ModelConfigEntry = {
  name: string;
  displayName: string;
  provider: ModelProvider;
  tag: string; // short descriptor shown under the name
  allowReasoningEffort: boolean;
  allowShowReasoning: boolean;
  defaultReasoning: string;
};

export type ModelConfig = Record<string, ModelConfigEntry>;

export const MODEL_CONFIG: ModelConfig = {
  "gpt-6.1-sol": {
    name: "gpt-6.1-sol",
    displayName: "GPT-6.1 Sol",
    tag: "Balanced",
    provider: "openai",
    allowReasoningEffort: true,
    allowShowReasoning: true,
    defaultReasoning: "medium",
  },
  "gpt-5.6-terra": {
    name: "gpt-5.6-terra",
    displayName: "GPT-5.6 Terra",
    tag: "Balanced",
    provider: "openai",
    allowReasoningEffort: true,
    allowShowReasoning: true,
    defaultReasoning: "medium",
  },
  "databricks-claude-opus-4-6": {
    name: "databricks-claude-opus-4-6",
    displayName: "Claude Opus 4.6",
    tag: "Advanced",
    provider: "anthropic",
    allowReasoningEffort: true,
    allowShowReasoning: true,
    defaultReasoning: "medium",
  },
  "databricks-claude-sonnet-4-6": {
    name: "databricks-claude-sonnet-4-6",
    displayName: "Claude Sonnet 4.6",
    tag: "Balanced",
    provider: "anthropic",
    allowReasoningEffort: true,
    allowShowReasoning: true,
    defaultReasoning: "medium",
  },
};

const DEFAULT_MODEL_KEY = "gpt-6.1-sol";
const FALLBACK_MODEL_KEY = "gpt-5.6-terra";

const getReasoningMapForModel = (
  modelKey: string,
): Record<ReasoningEffort, string> =>
  MODEL_REASONING_MAP[modelKey] ??
  MODEL_REASONING_MAP[DEFAULT_MODEL_KEY] ??
  MODEL_REASONING_MAP[FALLBACK_MODEL_KEY] ?? {
    [ReasoningEffort.Fast]: "low",
    [ReasoningEffort.Normal]: "medium",
    [ReasoningEffort.Thinking]: "high",
    [ReasoningEffort.Pro]: "xhigh",
  };

export const normalizeReasoningEffortForModel = (
  modelKey: string,
  effort: string | undefined | null,
): string => {
  const map = getReasoningMapForModel(modelKey);
  const mediumDefault = Object.values(map).includes("medium")
    ? "medium"
    : map[ReasoningEffort.Normal] ?? map[ReasoningEffort.Fast] ?? "low";

  if (effort && Object.values(map).includes(effort)) {
    return effort;
  }

  return mediumDefault;
};

export const getReasoningValueForModel = (
  modelKey: string,
  effort: ReasoningEffort,
): string => {
  const map = getReasoningMapForModel(modelKey);
  return normalizeReasoningEffortForModel(modelKey, map[effort]);
};

const getResolvedModelConfig = (
  modelKey: string,
  fallbackModel: ModelConfigEntry =
    MODEL_CONFIG[DEFAULT_MODEL_KEY] ?? MODEL_CONFIG[FALLBACK_MODEL_KEY],
): ModelConfigEntry => MODEL_CONFIG[modelKey] ?? fallbackModel;

const getResolvedReasoningEffort = (
  modelKey: string,
  effort: string,
): ReasoningEffort => {
  const normalizedEffort = normalizeReasoningEffortForModel(modelKey, effort);
  return (
    MODEL_REASONING_REVERSE_MAP[modelKey]?.[normalizedEffort] ||
    ReasoningEffort.Normal
  );
};

const getResolvedReasoningValue = (
  modelKey: string,
  effort: ReasoningEffort,
): string => getReasoningValueForModel(modelKey, effort);

const MODEL_NAMES = Object.keys(MODEL_CONFIG);

// ─── Component ──────────────────────────────────────────────────────────────

interface ModelSettingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    model: ModelConfigEntry,
    reasoningEffort: string,
    showReasoning: boolean,
  ) => void;
  model: ModelConfigEntry;
  reasoningEffort: string;
  showReasoning: boolean;
  buttonRef: React.RefObject<HTMLButtonElement>;
  availableModels?: string[];
}

const ModelSettingsPopup: React.FC<ModelSettingsPopupProps> = ({
  isOpen,
  onClose,
  onSave,
  model,
  reasoningEffort,
  showReasoning,
  buttonRef,
  availableModels,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const [selectedModelKey, setSelectedModelKey] = useState<string>(
    getResolvedModelConfig(model.name, model).name,
  );
  const [selectedModelConfig, setSelectedModelConfig] =
    useState<ModelConfigEntry>(getResolvedModelConfig(model.name, model));
  const [selectedReasoningEffort, setSelectedReasoningEffort] =
    useState<ReasoningEffort>(
      getResolvedReasoningEffort(model.name, reasoningEffort),
    );
  const [selectedShowReasoning, setSelectedShowReasoning] =
    useState<boolean>(showReasoning);

  // Filter to only show models the backend has deployed for this use case.
  // If availableModels is empty/undefined, show all (fallback).
  const visibleModelKeys = availableModels && availableModels.length > 0
    ? MODEL_NAMES.filter((key) => availableModels.includes(key))
    : MODEL_NAMES;

  // Sync from parent props
  useEffect(() => {
    setSelectedModelKey(getResolvedModelConfig(model.name, model).name);
    setSelectedModelConfig(getResolvedModelConfig(model.name, model));
    setSelectedReasoningEffort(
      getResolvedReasoningEffort(model.name, reasoningEffort),
    );
  }, [model, reasoningEffort]);

  // Click-outside close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  // ── Handlers ────────────────────────────────────────────

  const handleModelSelect = (modelKey: string) => {
    const cfg = getResolvedModelConfig(modelKey);
    setSelectedModelKey(modelKey);
    setSelectedModelConfig(cfg);

    const reason = cfg.allowReasoningEffort
      ? getResolvedReasoningValue(cfg.name, selectedReasoningEffort)
      : cfg.defaultReasoning;

    onSave(cfg, reason, selectedShowReasoning);
  };

  const handleReasoningSelect = (effort: ReasoningEffort) => {
    setSelectedReasoningEffort(effort);
    onSave(
      selectedModelConfig,
      getReasoningValueForModel(selectedModelConfig.name, effort),
      selectedShowReasoning,
    );
  };

  const showSettings = selectedModelConfig.allowReasoningEffort;

  return (
    <div className={styles.popup} ref={popupRef}>
      {/* ── Model list (scrollable) ──────────────────────── */}
      <div className={styles.modelList}>
        {visibleModelKeys.map((modelKey) => {
          const cfg = MODEL_CONFIG[modelKey];
          const isActive = selectedModelKey === modelKey;
          return (
            <button
              key={modelKey}
              className={`${styles.modelRow} ${isActive ? styles.modelRowActive : ""}`}
              onClick={() => handleModelSelect(modelKey)}
            >
              <span
                className={`${styles.modelRowIcon} ${isActive ? styles.modelRowIconActive : ""}`}
              >
                <ProviderIcon provider={cfg.provider} size={14} />
              </span>
              <span className={styles.modelRowText}>
                <span className={styles.modelRowName}>{cfg.displayName}</span>
                <span className={styles.modelRowTag}>{cfg.tag}</span>
              </span>
              {isActive && (
                <span className={styles.modelRowCheck}>
                  <CheckIcon />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Settings (reasoning) ─────────────────────────── */}
      {showSettings && (
        <>
          <div className={styles.divider} />
          <div className={styles.settingsSection}>
            {selectedModelConfig.allowReasoningEffort && (
              <div className={styles.settingsGroup}>
                <div className={styles.settingsLabel}>Reasoning Effort</div>
                <div className={styles.segmentedTrack}>
                  {Object.keys(ReasoningEffort).map((effortKey) => {
                    const effort = effortKey as ReasoningEffort;
                    return (
                      <button
                        key={effort}
                        className={`${styles.segmentedPill} ${
                          selectedReasoningEffort === effort
                            ? styles.segmentedPillActive
                            : ""
                        }`}
                        onClick={() => handleReasoningSelect(effort)}
                      >
                        {effort}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ModelSettingsPopup;